import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout, getAuthUser } from '../services/api';

const NAV_ITEMS = [
  // Active pages
  { path: '/', label: 'Fleet Overview', icon: '⊞' },
  { path: '/projects', label: 'Projects', icon: '◉' },
  { path: '/assignments', label: 'Assignment', icon: '⊞' },
  { path: '/field-feedback', label: 'Field Feedback', icon: '📝' },
  { path: '/robot-summary', label: 'Robot Summary', icon: '◎' },
  { path: '/errors', label: 'Error Stats', icon: '⊘' },
  { path: '/errors-trend', label: 'Errors Trend', icon: '⇑' },
  { path: '/analytical-reports', label: 'Analytical Reports', icon: '📋' },
  // v1.10.8 (2026-05-09) — User Management + Inventory Dashboard
  { path: '/users', label: 'Users', icon: '👥' },
  { path: '/inventory', label: 'Inventory', icon: '📦' },
  // Divider
  { divider: true },
  // Pages not yet built out — shown greyed as work-in-progress
  { path: '/parts', label: 'Spare Parts', icon: '⊡', wip: true },
  { path: '/technicians', label: 'Technicians', icon: '⊕', wip: true },
  { path: '/technician-mgmt', label: 'Manage Techs', icon: '⊕', wip: true },
  { path: '/logistics', label: 'Logistics', icon: '⊠', wip: true },
  { path: '/ab-testing', label: 'A/B Testing', icon: '⊜', wip: true },
  { path: '/regions', label: 'Regions', icon: '⊛', wip: true },
  { path: '/training', label: 'Training', icon: '⊙', wip: true },
  { path: '/analytics', label: 'Analytics', icon: '⊗', wip: true },
];

export default function Layout({ children, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getAuthUser();

  const handleLogout = async () => {
    await logout();
    if (onLogout) onLogout();
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      <nav style={styles.sidebar}>
        <div style={styles.logo}>
          <img src="/airtouch-logo.svg" alt="AirTouch" style={styles.logoImg} />
          <span style={styles.logoSub}>MAINTENANCE</span>
        </div>
        <div style={styles.navList}>
          {NAV_ITEMS.map((item, i) => {
            if (item.divider) {
              return <div key={`div-${i}`} style={styles.navDivider} />;
            }
            const active = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  ...styles.navItem,
                  ...(item.wip ? styles.navItemWip : {}),
                  ...(active ? styles.navItemActive : {}),
                }}
                title={item.wip ? 'Work in progress' : undefined}
              >
                <span style={styles.navIcon}>{item.icon}</span>
                {item.label}
                {item.wip && <span style={styles.wipTag}>WIP</span>}
              </Link>
            );
          })}
        </div>
        <div style={styles.sidebarFooter}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, flex: 1}}>
            <div style={styles.statusDot} />
            <span style={styles.statusText}>{user || 'Online'}</span>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn} title="Logout">Logout</button>
        </div>
      </nav>
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    fontFamily: "'Inter', -apple-system, sans-serif",
    background: '#f5f6fa',
    color: '#2d3436',
  },
  sidebar: {
    width: 220,
    background: '#1a1a2e',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  logo: {
    padding: '24px 20px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  logoImg: {
    width: '100%',
    maxWidth: 180,
    height: 'auto',
    filter: 'brightness(1.1)',
  },
  logoSub: {
    fontSize: 10,
    opacity: 0.45,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    fontWeight: 500,
  },
  navList: {
    flex: 1,
    padding: '12px 0',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 20px',
    color: 'rgba(255,255,255,0.6)',
    textDecoration: 'none',
    fontSize: 14,
    transition: 'all 0.15s',
    borderLeft: '3px solid transparent',
  },
  navItemActive: {
    color: '#fff',
    background: 'rgba(255,255,255,0.08)',
    borderLeftColor: '#6c5ce7',
  },
  navItemWip: {
    color: 'rgba(255,255,255,0.28)',
  },
  wipTag: {
    marginLeft: 'auto',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.5,
    padding: '1px 5px',
    borderRadius: 4,
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.4)',
  },
  navDivider: {
    height: 1,
    margin: '10px 16px',
    background: 'rgba(255,255,255,0.08)',
  },
  navIcon: {
    fontSize: 16,
    width: 20,
    textAlign: 'center',
  },
  sidebarFooter: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    opacity: 0.6,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#00b894',
  },
  statusText: {
    fontSize: 12,
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    padding: '4px 10px',
    cursor: 'pointer',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
  },
};
